import store from '../store';
import { buildAuthorizeUrl } from '../api/oauthClient';
import { isSafeExternalUrl } from '../utils/redirectUtils';

function saveQueryParams(to, next) {
	if (to.query.mf || to.query.as || to.query.cr || to.query.y || to.query.rt) {
		localStorage.ffaUserRego = JSON.stringify({
			mf: to.query.mf ? to.query.mf : null,
			as: to.query.as ? to.query.as : null,
			cr: to.query.cr ? to.query.cr : null,
			y: to.query.y ? to.query.y : null,
			rt: to.query.rt ? to.query.rt : null
		});

		localStorage.regoInProgress = true;

		next({
			...to,
			query: {}
		});
	}
}

function saveRedirectUri(to, next) {
	if (to.query.redirect_uri) {
		// Security: validate redirect_uri against the Football Australia domain
		// allowlist before storing. An attacker could craft a URL with an arbitrary
		// redirect_uri and trick users into being sent to a malicious site after login.
		// Silently drop invalid URIs — the partner domain must be in redirectUtils.js.
		if (!isSafeExternalUrl(to.query.redirect_uri)) {
			next({
				...to,
				query: {}
			});
			return;
		}

		localStorage.ffaRedirectUri = to.query.redirect_uri;

		if (to.query.client_id) {
			localStorage.ffaClientId = to.query.client_id;
			window.CognitoClientIdPlayfootball = localStorage.ffaClientId;
		}

		if (to.query.platform) {
			localStorage.ffaPlatform = to.query.platform;
		}
		next({
			...to,
			query: {}
		});
	}
}

function doRedirectUri(user) {
	const url = new URL(window.location.origin);
	url.pathname = '/oauth2/complete';
	url.searchParams.set('client_id', CognitoClientIdPlayfootball);
	url.searchParams.set('redirect_uri', localStorage.ffaRedirectUri);
	url.searchParams.set('id_token', user.idToken);

	localStorage.removeItem('ffaRedirectUri');
	localStorage.removeItem('ffaClientId');
	localStorage.removeItem('ffaPlatform');
	localStorage.removeItem('ffaUserRego');
	window.location.href = url.href;
}

function checkRegoOffers(user) {
  return new Promise((resolve) => {
		if (localStorage.ffaUserRego) {
			if (user.onboardingcomplete !== 'Skipped' && !localStorage.ffaRedirectUri) {
				store.dispatch('onboarding/SKIP_PREFERENCES_SELECTION_REGO').then(() => {
					resolve();
				});
			} else {
				resolve();
			}
		} else {
      resolve();
    }
  });
}

function checkRedirectUri(user) {
  return new Promise ((resolve) => {
		if (localStorage.ffaRedirectUri) {
			if (localStorage.ffaPlatform === CognitoPlatforms.PlayFootball) {
				if (user.onboardingcomplete !== 'Skipped') {
					store.dispatch('onboarding/SKIP_PREFERENCES_SELECTION_REGO');
				}
				doRedirectUri(user);
			} else {
        doRedirectUri(user);
			}
		} else {
        resolve();
		}
	});
}

/**
 * Resolve the current authenticated user.
 *
 * Lookup order:
 *   1. Valid (non-expired) user already in Vuex state → resolve immediately
 *   2. Dispatch user/RETRIEVE_USER to hydrate from stored tokens
 *      a. Success → resolve with the freshly loaded user
 *      b. Failure (no tokens) → attempt silent SSO via prompt=none redirect if:
 *           - OAuth is enabled (window.OAuthConfig?.enabled)
 *           - Current route is not /oauth/callback (prevents redirect loops)
 *         Otherwise → resolve undefined so the page renders with a login button
 *
 * @param {string} toPath - The target route path (used as state in the OAuth redirect)
 * @param {string} toName - The target route name (used to detect /oauth/callback)
 * @returns {Promise<object|undefined>} Resolves with the user object or undefined
 */
function getUser(toPath, toName, toFullPath) {
	const stateUser = store.state.user.user;

	return new Promise((resolve) => {
		if (stateUser) {
			if ((stateUser.exp * 1000) > Date.now()) {
				resolve(stateUser);
				return;
			}
		}

		store
			.dispatch('user/RETRIEVE_USER')
			.then(() => {
				resolve(store.state.user.user);
			})
			.catch(() => {
				const oauthEnabled = window.OAuthConfig && window.OAuthConfig.enabled;

				if (!oauthEnabled) {
					resolve();
					return;
				}

				// Do not redirect to prompt=none when already on the callback route —
				// OAuthCallbackPage handles the code exchange and will redirect itself.
				if (toPath === '/oauth/callback' || toName === 'oauthCallback') {
					resolve();
					return;
				}

				// Do not redirect to prompt=none for guest-only routes (registration and password reset flows).
				// These pages must remain accessible to unauthenticated users.
				//
				// /callback-oneclick is also guest-accessible: the OneClick JWT in the query
				// string is its own proof of verification. The mobile QR code flow lands here
				// without a KeeperFA session — forcing auth would lose the token.
				//
				// /subscriptions is guest-accessible too: unsubscribe links are opened from
				// email by recipients who are typically not signed in. The :contactId in the
				// path is the identifier, and the endpoint it posts to
				// (/api/v1/subscription/unsubscribe) is public, so no session is required.
				if (toPath.startsWith('/register') || toPath.startsWith('/reset-password') || toPath.startsWith('/callback-oneclick') || toPath.startsWith('/subscriptions')) {
					resolve();
					return;
				}

				// Store the intended destination so OAuthCallbackPage can restore it
				// after a successful silent SSO code exchange.
				// Use toFullPath (includes query params) so tokens/params in the URL are preserved.
				sessionStorage.setItem('oauth_pre_auth_path', toFullPath);

				// Attempt silent SSO — if Cognito has an active session, it will
				// redirect back to /oauth/callback with a code and no user interaction.
				// If there is no session, Cognito returns error=login_required which
				// OAuthCallbackPage handles by redirecting to full login.
				buildAuthorizeUrl({ prompt: 'none', state: toFullPath }).then((url) => {
					window.location.href = url;
					// Do NOT call resolve() — a full-page navigation is in progress.
				});
			});
	});
}

export default function (to, from, next) {
	// scroll to top... simulate a new page load
	window.scrollTo(0,0);
	saveRedirectUri(to, next);
	saveQueryParams(to, next);

	store
		.dispatch('CHANGE_LOADING_STATE', true)
		.then(() => {
			getUser(to.path, to.name, to.fullPath)
				.then(user => {
					if (user) {
            checkRegoOffers(user)
              .then(() => {
                checkRedirectUri(user).then(() => next())
              });
					} else {
						next();
					}
				});
		});
}
